// src/pages/ReservationShow.jsx
import React, { useEffect, useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { http } from '@/api/http';

import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Divider,
  CircularProgress,
  IconButton
} from '@mui/material';
import { ArrowLeftOutlined } from '@ant-design/icons';

const statusLabel = {
  pending: 'Pendiente',
  locked: 'Bloqueada',
  canceled: 'Cancelada',
  converted: 'Convertida'
};

function statusColor(status) {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'locked':
      return 'info';
    case 'canceled':
      return 'error';
    case 'converted':
      return 'success';
    default:
      return 'default';
  }
}

export default function ReservationShow() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let mounted = true;

    async function fetchDetail() {
      setLoading(true);
      setErrorMsg('');
      try {
        const { data } = await http.get(`/reservations/${id}`);
        const res = data?.data || data;

        if (!mounted) return;
        setDetail(res);
      } catch (err) {
        console.error('Error al cargar la separación', err);
        if (!mounted) return;
        setErrorMsg('No se pudo cargar la separación.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchDetail();
    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowLeftOutlined />}
          onClick={() => navigate('/reservations')}
          sx={{ mb: 2 }}
        >
          Volver
        </Button>
        <Typography variant="h6" color="error">
          {errorMsg || 'Separación no encontrada.'}
        </Typography>
      </Box>
    );
  }

  const reservationNumber = detail.reservation_number || `SEP-${detail.id}`;
  const reservationDate = detail.reservation_date
    ? new Date(detail.reservation_date).toLocaleDateString()
    : '-';

  // Agrupamos por personaje igual que en la tabla de la lista
  const groupedByCharacter = (detail.items || []).reduce((acc, item) => {
    const key = item.character?.name || 'Sin personaje';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/reservations')} sx={{ mr: 1 }}>
          <ArrowLeftOutlined />
        </IconButton>

        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">
            Separación {reservationNumber}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fecha de separación: {reservationDate}
          </Typography>
        </Box>

        <Chip
          size="medium"
          label={statusLabel[detail.status] ?? detail.status}
          color={statusColor(detail.status)}
        />
      </Box>

      <Grid container spacing={2}>
        {/* Datos del cliente */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Datos del cliente
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <strong>Nombre:</strong>{' '}
                {detail.client_full_name || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>DNI:</strong>{' '}
                {detail.client_dni || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Teléfono:</strong>{' '}
                {detail.client_phone || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Dirección:</strong>{' '}
                {detail.client_address || '-'}
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        {/* Datos de la separación */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Información de la separación
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <strong>N° de ítems:</strong>{' '}
                {detail.items?.length ?? 0}
              </Typography>
              <Typography variant="body2">
                <strong>Estado:</strong>{' '}
                {statusLabel[detail.status] ?? detail.status}
              </Typography>
              {detail.notes && (
                <Typography variant="body2">
                  <strong>Notas:</strong> {detail.notes}
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabla de ítems */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Ítems reservados
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {Object.keys(groupedByCharacter).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay ítems registrados en esta separación.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Prenda</TableCell>
                <TableCell>Personaje</TableCell>
                <TableCell>Talla</TableCell>
                <TableCell>Condición</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell align="right">Precio unit.</TableCell>
                <TableCell align="right">Subtotal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(groupedByCharacter).map(
                ([characterName, itemsGroup]) => (
                  <Fragment key={characterName}>
                    {/* Fila de título del personaje */}
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Personaje: {characterName}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {itemsGroup.map((item) => {
                      const unitPriceNum =
                        item.unit_price != null
                          ? Number(item.unit_price)
                          : null;
                      const qty = item.qty ?? 0;
                      const subtotal =
                        unitPriceNum != null && !Number.isNaN(unitPriceNum)
                          ? unitPriceNum * qty
                          : null;

                      return (
                        <TableRow key={item.id}>
                          <TableCell>
                            {item.garment?.name ?? `#${item.garment_id}`}
                            {item.garment?.sku && (
                              <Typography variant="caption" display="block">
                                SKU: {item.garment.sku}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{item.character?.name ?? '-'}</TableCell>
                          <TableCell>{item.size_label ?? '-'}</TableCell>
                          <TableCell>{item.condition}</TableCell>
                          <TableCell align="right">{qty}</TableCell>
                          <TableCell align="right">
                            {unitPriceNum != null &&
                            !Number.isNaN(unitPriceNum)
                              ? `S/ ${unitPriceNum.toFixed(2)}`
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {subtotal != null && !Number.isNaN(subtotal)
                              ? `S/ ${subtotal.toFixed(2)}`
                              : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </Fragment>
                )
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
